import dayjs from 'dayjs';
import { showSaveFilePicker } from 'native-file-system-adapter';

export interface DownloadOptions {
  url: string;
  deviceName?: string;
  timestamp?: string | number;
  rotation?: number; // Video rotation in degrees
  onProgress?: (progress: number) => void;
}

export interface DownloadFromResponseOptions {
  response: Response;
  deviceName?: string;
  timestamp?: string | number;
  onProgress?: (progress: number) => void;
}

export interface DownloadResult {
  success: boolean;
  error?: string;
  cleanupPerformed?: boolean;
}

interface DownloadToolsConfig {
  baseUrl: string;
  csrfToken: string;
}

const sanitizeFileName = (name: string): string => name.replace(/[^a-z0-9\-_]/gi, '_').toLowerCase();

const isDashOrHls = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();

    // Check if pathname ends with .mpd or .m3u8
    if (pathname.endsWith('.mpd') || pathname.endsWith('.m3u8')) {
      return true;
    }

    // Also check for common streaming endpoint patterns that might not have file extensions
    const fullUrl = url.toLowerCase();
    return (
      fullUrl.includes('getdashmanifest') ||
      fullUrl.includes('gethlsmanifest') ||
      fullUrl.includes('manifest.mpd') ||
      fullUrl.includes('playlist.m3u8') ||
      fullUrl.includes('kinesisvideo')
    ); // Add Kinesis Video Streams detection
  } catch {
    return false;
  }
};

export const getFileExtension = (url: string): string => {
  try {
    const urlPath = new URL(url).pathname;
    const extension = urlPath.split('.').pop()?.toLowerCase();

    // For DASH/HLS, default to mp4 as the downloaded format
    if (extension === 'mpd' || extension === 'm3u8') {
      return 'mp4';
    }

    return extension || 'mp4';
  } catch {
    return 'mp4';
  }
};

export const generateFileName = (deviceName?: string, timestamp?: string | number): string => {
  let filename = 'video';

  if (deviceName) {
    filename = sanitizeFileName(deviceName);
  }

  if (timestamp) {
    const timeStr =
      typeof timestamp === 'number' ? dayjs(timestamp).format('YYYY-MM-DD_HH-mm-ss') : sanitizeFileName(timestamp);
    filename += `-${timeStr}`;
  }

  return filename;
};

export const DownloadTools = ({ baseUrl, csrfToken }: DownloadToolsConfig) => {
  const downloadWithNativeFilePicker = async (options: DownloadOptions): Promise<DownloadResult> => {
    const { url, deviceName, timestamp, rotation = 0, onProgress } = options;

    // Generate filename
    const extension = getFileExtension(url);
    const baseFilename = generateFileName(deviceName, timestamp);
    const suggestedName = `${baseFilename}.${extension}`;

    // Show native file picker (Chrome/Edge) or fallback to traditional download
    const fileHandle = await showSaveFilePicker({
      suggestedName,
      types: [
        {
          description: 'Video files',
          accept: {
            'video/mp4': ['.mp4'],
            'video/webm': ['.webm'],
            'video/mov': ['.mov'],
            'video/avi': ['.avi'],
            'application/dash+xml': ['.mpd'],
            'application/x-mpegURL': ['.m3u8'],
          },
        },
      ],
    });

    try {
      // Check if this is a DASH or HLS URL that needs special handling
      const isStreamingFormat = isDashOrHls(url);

      let response: Response;

      // For DASH/HLS URLs, skip direct fetch and use server proxy with FFmpeg processing
      if (isStreamingFormat) {
        response = await fetch(`/${baseUrl}/api/v1/download-proxy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            url,
            headers: {},
            isDashOrHls: isStreamingFormat,
            rotation,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
      } else {
        // For regular video files, most will be cross-origin (S3, CDNs, etc.)
        // Use server proxy for reliable downloads that handle CORS
        response = await fetch(`/${baseUrl}/api/v1/download-proxy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            url,
            headers: {},
            isDashOrHls: false,
            rotation,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }
      }

      // Successfully got response

      // Get content length for progress tracking
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      let loaded = 0;

      // Create writable stream to the file
      const writableStream = await fileHandle.createWritable();

      let downloadCompleted = false;

      try {
        // Create transform stream for progress tracking
        const transformStream = new TransformStream({
          transform(chunk, controller) {
            loaded += chunk.byteLength;
            if (onProgress) {
              if (total > 0) {
                // Determinate progress when content length is known
                const progress = Math.round((loaded / total) * 100);
                onProgress(progress);
              } else {
                // Indeterminate progress - show that download is active
                // Use a small positive number to indicate activity
                onProgress(-1); // -1 will indicate indeterminate progress
              }
            }
            controller.enqueue(chunk);
          },
        });

        // Pipe response through transform stream to file
        if (!response.body) {
          throw new Error('Response body is null or undefined');
        }

        await response.body.pipeThrough(transformStream).pipeTo(writableStream);

        downloadCompleted = true;
        return { success: true };
      } catch (error) {
        // Clean up the partially downloaded file
        try {
          if (!downloadCompleted) {
            // Close the writable stream first
            try {
              await writableStream.close();
            } catch (_closeError) {
              // Error closing writable stream - continue with cleanup
            }

            // Remove the file by writing an empty stream to it
            // This effectively deletes the content and marks it for cleanup
            if (fileHandle && 'remove' in fileHandle) {
              // If the File System Access API supports file removal
              await (fileHandle as FileSystemFileHandle & { remove(): Promise<void> }).remove();
            } else {
              // Fallback: overwrite with empty content
              const cleanupStream = await (fileHandle as FileSystemFileHandle).createWritable();
              await cleanupStream.close();
            }
          }
        } catch (_cleanupError) {
          // Don't throw cleanup errors - continue with original error
        }

        // Re-throw the original error
        throw error;
      }
    } catch (error) {
      // Handle errors that occur before streaming starts (e.g., fetch failures)
      try {
        // Clean up the empty file that was created by showSaveFilePicker
        if (fileHandle && 'remove' in fileHandle) {
          // If the File System Access API supports file removal
          await (fileHandle as FileSystemFileHandle & { remove(): Promise<void> }).remove();
        } else {
          // Fallback: create and immediately close empty stream to clear the file
          const cleanupStream = await (fileHandle as FileSystemFileHandle).createWritable();
          await cleanupStream.close();
        }
      } catch (_cleanupError) {
        // Don't throw cleanup errors - continue with original error
      }

      // Re-throw the original error
      throw error;
    }
  };

  const downloadFile = async (options: DownloadOptions): Promise<DownloadResult> => {
    const { url } = options;

    if (!url) {
      return { success: false, error: 'No URL provided' };
    }

    try {
      // Use native file picker with streaming (handles CORS via server proxy)
      return await downloadWithNativeFilePicker(options);
    } catch (error) {
      // Handle user cancellation (no cleanup needed - user explicitly cancelled)
      if (error instanceof Error && error.name === 'AbortError') {
        return { success: false, error: 'Download cancelled by user' };
      }

      // For all other errors, return the error (no more fallbacks)
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      return { success: false, error: errorMsg };
    }
  };

  const downloadFromResponse = async (options: DownloadFromResponseOptions): Promise<DownloadResult> => {
    const { response, deviceName, timestamp, onProgress } = options;

    try {
      // Generate filename - assume mp4 for cached video
      const baseFilename = generateFileName(deviceName, timestamp);
      const suggestedName = `${baseFilename}.mp4`;

      // Show native file picker
      const fileHandle = await showSaveFilePicker({
        suggestedName,
        types: [
          {
            description: 'Video files',
            accept: {
              'video/mp4': ['.mp4'],
              'video/webm': ['.webm'],
              'video/mov': ['.mov'],
              'video/avi': ['.avi'],
            },
          },
        ],
      });

      // Get content length for progress tracking
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      let loaded = 0;

      // Create writable stream to the file
      const writableStream = await fileHandle.createWritable();

      let downloadCompleted = false;

      try {
        // Create transform stream for progress tracking
        const transformStream = new TransformStream({
          transform(chunk, controller) {
            loaded += chunk.byteLength;
            if (onProgress) {
              if (total > 0) {
                const progress = Math.round((loaded / total) * 100);
                onProgress(progress);
              } else {
                onProgress(-1); // Indeterminate progress
              }
            }
            controller.enqueue(chunk);
          },
        });

        // Pipe response through transform stream to file
        if (!response.body) {
          throw new Error('Response body is null or undefined');
        }

        await response.body.pipeThrough(transformStream).pipeTo(writableStream);

        downloadCompleted = true;
        return { success: true };
      } catch (error) {
        // Clean up the partially downloaded file
        try {
          if (!downloadCompleted) {
            try {
              await writableStream.close();
            } catch (_closeError) {
              // Error closing writable stream - continue with cleanup
            }

            if (fileHandle && 'remove' in fileHandle) {
              await (fileHandle as FileSystemFileHandle & { remove(): Promise<void> }).remove();
            } else {
              const cleanupStream = await (fileHandle as FileSystemFileHandle).createWritable();
              await cleanupStream.close();
            }
          }
        } catch (_cleanupError) {
          // Don't throw cleanup errors - continue with original error
        }

        throw error;
      }
    } catch (error) {
      // Handle user cancellation
      if (error instanceof Error && error.name === 'AbortError') {
        return { success: false, error: 'Download cancelled by user' };
      }

      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      return { success: false, error: errorMsg };
    }
  };

  return {
    downloadFile,
    downloadFromResponse,
  };
};
